"use client"

import React from 'react'
import Link from 'next/link'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { cn } from '@/lib/utils'
import { Check, Loader2, Zap } from 'lucide-react'
import { usePublicSubscriptionPlans } from '@/hooks/use-subscription-plans'
import { PublicSubscriptionPlan } from '@/lib/types/subscription.types'

type Billing = 'monthly' | 'yearly'

function formatAmount(amountCents: number): string {
  const dollars = amountCents / 100
  return Number.isInteger(dollars) ? `$${dollars}` : `$${dollars.toFixed(2)}`
}

function periodLabel(plan: PublicSubscriptionPlan): string {
  if (plan.billing === 'yearly') return '/year'
  if (plan.billing === 'monthly') return '/mo'
  return ''
}

export default function PricingPlans() {
  const { data, isLoading, isError } = usePublicSubscriptionPlans()
  const [billing, setBilling] = React.useState<Billing>('monthly')

  const catalog = data?.data
  const plans = catalog?.plans ?? []
  const hasYearly = plans.some((plan) => plan.billing === 'yearly')
  const hasMonthly = plans.some((plan) => plan.billing === 'monthly')
  const savings = catalog?.yearly_savings_percent

  React.useEffect(() => {
    if (!hasMonthly && hasYearly) {
      setBilling('yearly')
    }
  }, [hasMonthly, hasYearly])

  const visiblePlans = plans.filter((plan) => {
    if (plan.billing === 'free') return true
    return plan.billing === billing
  })

  return (
    <div className="space-y-8">
      {(hasMonthly || hasYearly) && (
        <div className="flex items-center justify-center gap-3">
          <span className={cn('text-sm', billing === 'monthly' ? 'text-foreground' : 'text-muted-foreground')}>Monthly</span>
          <button
            onClick={() => setBilling(billing === 'monthly' ? 'yearly' : 'monthly')}
            className="relative inline-flex h-9 w-16 items-center rounded-full border border-border bg-background transition-colors"
            aria-label="Toggle billing period"
            disabled={!hasMonthly || !hasYearly}
          >
            <span
              className={cn(
                'absolute h-7 w-7 rounded-full bg-gradient-primary shadow-subtle transition-transform',
                billing === 'yearly' ? 'translate-x-8' : 'translate-x-1'
              )}
            />
            <span className="sr-only">Toggle billing</span>
          </button>
          <span className={cn('text-sm', billing === 'yearly' ? 'text-foreground' : 'text-muted-foreground')}>Yearly</span>
          {typeof savings === 'number' && savings > 0 && (
            <Badge className="ml-1 bg-[hsl(var(--primary-blue))] text-white">Save {savings}%</Badge>
          )}
        </div>
      )}

      {isLoading && (
        <div className="flex justify-center py-10">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      )}

      {isError && (
        <p className="text-center text-sm text-muted-foreground">
          Pricing is unavailable right now. Please try again shortly.
        </p>
      )}

      {!isLoading && !isError && (
        <div className={cn(
          'grid gap-6 mx-auto',
          visiblePlans.length > 1 ? 'md:grid-cols-2 max-w-3xl' : 'md:grid-cols-1 max-w-lg'
        )}>
          {visiblePlans.map((plan) => (
            <Card
              key={plan.id}
              className={cn(
                'relative border-border/60 hover:shadow-elegant transition-all',
                plan.recommended && 'ring-1 ring-[hsl(var(--primary-blue))]'
              )}
            >
              {plan.recommended && (
                <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[hsl(var(--primary-blue))] text-white flex items-center gap-1">
                  <Zap className="h-3.5 w-3.5" /> Recommended
                </Badge>
              )}
              <CardHeader>
                <CardTitle>{plan.name}</CardTitle>
                <CardDescription>{plan.description}</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="mb-4">
                  <div className="text-4xl font-bold text-foreground">
                    {plan.billing === 'free' ? 'Free' : formatAmount(plan.amount_cents)}
                    {plan.billing !== 'free' && (
                      <span className="text-sm font-normal text-muted-foreground">{periodLabel(plan)}</span>
                    )}
                  </div>
                </div>
                <ul className="space-y-2 mb-6">
                  {plan.features.map((feature) => (
                    <li key={feature} className="flex items-start gap-2">
                      <Check className="h-4 w-4 text-success-green mt-0.5" />
                      <span className="text-sm text-foreground">{feature}</span>
                    </li>
                  ))}
                </ul>
                <Button variant={plan.recommended ? 'hero' : 'outline'} className="w-full" asChild>
                  <Link href={plan.cta_href}>{plan.cta_label}</Link>
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  )
}
